/**
 * Icon geometry for the approved canideal.org pages, rendered through AppIcon.
 * `lucide` entries are Lucide v0.468.0 (ISC licence, https://lucide.dev) — the
 * exact subset canideal.org ships; the rest are canideal.org's own trust icons.
 */

type IconAttrs = Record<string, string>;
export type IconNode = ReadonlyArray<readonly ['path' | 'circle' | 'rect' | 'ellipse', IconAttrs]>;

export interface CidIconDef {
  nodes: IconNode;
  strokeWidth: number;
  /** Default "0 0 24 24". */
  viewBox?: string;
  /** Default true (round caps/joins); false keeps the SVG defaults. */
  round?: boolean;
}

const p = (d: string) => ['path', { d }] as const;
const lucide = (...nodes: IconNode[number][]): CidIconDef => ({ nodes, strokeWidth: 2 });
const trust = (...nodes: IconNode[number][]): CidIconDef => ({ nodes, strokeWidth: 1.6 });
/** canideal.org CanIDeal IQ icons: square caps (no round linecap in iq.html). */
const iq = (...nodes: IconNode[number][]): CidIconDef => ({
  nodes,
  strokeWidth: 1.6,
  round: false,
});

export const CID_ICONS = {
  leaf: lucide(
    p('M11 20A7 7 0 0 1 9.8 6.1C15.5 5 17 4.48 19 2c1 2 2 4.18 2 8 0 5.5-4.78 10-10 10Z'),
    p('M2 21c0-3 1.85-5.36 5.08-6C9.5 14.52 12 13 13 12')
  ),
  'flask-conical': lucide(
    p(
      'M14 2v6a2 2 0 0 0 .245.96l5.51 10.08A2 2 0 0 1 18 22H6a2 2 0 0 1-1.755-2.96l5.51-10.08A2 2 0 0 0 10 8V2'
    ),
    p('M6.453 15h11.094'),
    p('M8.5 2h7')
  ),
  droplet: lucide(
    p(
      'M12 22a7 7 0 0 0 7-7c0-2-1-3.9-3-5.5s-3.5-4-4-6.5c-.5 2.5-2 4.9-4 6.5C6 11.1 5 13 5 15a7 7 0 0 0 7 7z'
    )
  ),
  package: lucide(
    p(
      'M11 21.73a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73z'
    ),
    p('M12 22V12'),
    p('m3.3 7 7.703 4.734a2 2 0 0 0 1.994 0L20.7 7'),
    p('m7.5 4.27 9 5.15')
  ),
  factory: lucide(
    p('M2 20a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V8l-7 5V8l-7 5V4a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2Z'),
    p('M17 18h1'),
    p('M12 18h1'),
    p('M7 18h1')
  ),
  users: lucide(
    p('M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2'),
    ['circle', { cx: '9', cy: '7', r: '4' }],
    p('M22 21v-2a4 4 0 0 0-3-3.87'),
    p('M16 3.13a4 4 0 0 1 0 7.75')
  ),
  handshake: lucide(
    p('m11 17 2 2a1 1 0 1 0 3-3'),
    p(
      'm14 14 2.5 2.5a1 1 0 1 0 3-3l-3.88-3.88a3 3 0 0 0-4.24 0l-.88.88a1 1 0 1 1-3-3l2.81-2.81a5.79 5.79 0 0 1 7.06-.87l.47.28a2 2 0 0 0 1.42.25L21 4'
    ),
    p('m21 3 1 11h-2'),
    p('M3 3 2 14l6.5 6.5a1 1 0 1 0 3-3'),
    p('M3 4h8')
  ),
  'globe-2': lucide(
    p('M21.54 15H17a2 2 0 0 0-2 2v4.54'),
    p('M7 3.34V5a3 3 0 0 0 3 3a2 2 0 0 1 2 2c0 1.1.9 2 2 2a2 2 0 0 0 2-2c0-1.1.9-2 2-2h3.17'),
    p('M11 21.95V18a2 2 0 0 0-2-2a2 2 0 0 1-2-2v-1a2 2 0 0 0-2-2H2.05'),
    ['circle', { cx: '12', cy: '12', r: '10' }]
  ),
  radar: lucide(
    p('M19.07 4.93A10 10 0 0 0 6.99 3.34'),
    p('M4 6h.01'),
    p('M2.29 9.62A10 10 0 1 0 21.31 8.35'),
    p('M16.24 7.76A6 6 0 1 0 8.23 16.67'),
    p('M12 18h.01'),
    p('M17.99 11.66A6 6 0 0 1 15.77 16.67'),
    ['circle', { cx: '12', cy: '12', r: '2' }],
    p('m13.41 10.59 5.66-5.66')
  ),
  'package-open': lucide(
    p('M12 22v-9'),
    p(
      'M15.17 2.21a1.67 1.67 0 0 1 1.63 0L21 4.57a1.93 1.93 0 0 1 0 3.36L8.82 14.79a1.655 1.655 0 0 1-1.64 0L3 12.43a1.93 1.93 0 0 1 0-3.36z'
    ),
    p(
      'M20 13v3.87a2.06 2.06 0 0 1-1.11 1.83l-6 3.08a1.93 1.93 0 0 1-1.78 0l-6-3.08A2.06 2.06 0 0 1 4 16.87V13'
    ),
    p(
      'M21 12.43a1.93 1.93 0 0 0 0-3.36L8.83 2.2a1.64 1.64 0 0 0-1.63 0L3 4.57a1.93 1.93 0 0 0 0 3.36l12.18 6.86a1.636 1.636 0 0 0 1.63 0z'
    )
  ),
  'bar-chart-3': lucide(p('M3 3v16a2 2 0 0 0 2 2h16'), p('M18 17V9'), p('M13 17V5'), p('M8 17v-3')),
  store: lucide(
    p('m2 7 4.41-4.41A2 2 0 0 1 7.83 2h8.34a2 2 0 0 1 1.42.59L22 7'),
    p('M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8'),
    p('M15 22v-4a2 2 0 0 0-2-2h-2a2 2 0 0 0-2 2v4'),
    p('M2 7h20'),
    p(
      'M22 7v3a2 2 0 0 1-2 2a2.7 2.7 0 0 1-1.59-.63.7.7 0 0 0-.82 0A2.7 2.7 0 0 1 16 12a2.7 2.7 0 0 1-1.59-.63.7.7 0 0 0-.82 0A2.7 2.7 0 0 1 12 12a2.7 2.7 0 0 1-1.59-.63.7.7 0 0 0-.82 0A2.7 2.7 0 0 1 8 12a2.7 2.7 0 0 1-1.59-.63.7.7 0 0 0-.82 0A2.7 2.7 0 0 1 4 12a2 2 0 0 1-2-2V7'
    )
  ),
  'messages-square': lucide(
    p('M14 9a2 2 0 0 1-2 2H6l-4 4V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2z'),
    p('M18 9h2a2 2 0 0 1 2 2v11l-4-4h-6a2 2 0 0 1-2-2v-1')
  ),
  'chart-no-axes-combined': lucide(
    p('M12 16v5'),
    p('M16 14v7'),
    p('M20 10v11'),
    p('m22 3-8.646 8.646a.5.5 0 0 1-.708 0L9.354 8.354a.5.5 0 0 0-.707 0L2 15'),
    p('M4 18v3'),
    p('M8 14v7')
  ),
  network: lucide(
    ['rect', { x: '16', y: '16', width: '6', height: '6', rx: '1' }],
    ['rect', { x: '2', y: '16', width: '6', height: '6', rx: '1' }],
    ['rect', { x: '9', y: '2', width: '6', height: '6', rx: '1' }],
    p('M5 16v-3a1 1 0 0 1 1-1h12a1 1 0 0 1 1 1v3'),
    p('M12 12V8')
  ),
  // Markets & Pricing (lucide.createIcons on markets.html); `sprout` is the trust icon (Crop)
  sproutLeaf: lucide(
    p('M7 20h10'),
    p('M10 20c5.5-2.5.8-6.4 3-10'),
    p('M9.5 9.4c1.1.8 1.8 2.2 2.3 3.7-2 .4-3.5.4-4.8-.3-1.2-.6-2.3-1.9-3-4.2 2.8-.5 4.4 0 5.5.8z'),
    p('M14.1 6a7 7 0 0 0-1.1 4c1.9-.1 3.3-.6 4.3-1.4 1-1 1.6-2.3 1.7-4.6-2.7.1-4 1-4.9 2z')
  ),
  droplets: lucide(
    p(
      'M7 16.3c2.2 0 4-1.83 4-4.05 0-1.16-.57-2.26-1.71-3.19S7.29 6.75 7 5.3c-.29 1.45-1.14 2.84-2.29 3.76S3 11.1 3 12.25c0 2.22 1.8 4.05 4 4.05z'
    ),
    p(
      'M12.56 6.6A10.97 10.97 0 0 0 14 3.02c.5 2.5 2 4.9 4 6.5s3 3.5 3 5.5a6.98 6.98 0 0 1-11.91 4.97'
    )
  ),
  bean: lucide(
    p(
      'M10.165 6.598C9.954 7.478 9.64 8.36 9 9c-.64.64-1.521.954-2.402 1.165A6 6 0 0 0 8 22c7.732 0 14-6.268 14-14a6 6 0 0 0-11.835-1.402Z'
    ),
    p('M5.341 10.62a4 4 0 1 0 5.279-5.28')
  ),
  layers: lucide(
    p(
      'M12.83 2.18a2 2 0 0 0-1.66 0L2.6 6.08a1 1 0 0 0 0 1.83l8.58 3.91a2 2 0 0 0 1.66 0l8.58-3.9a1 1 0 0 0 0-1.83z'
    ),
    p('M2 12a1 1 0 0 0 .58.91l8.6 3.91a2 2 0 0 0 1.65 0l8.58-3.9A1 1 0 0 0 22 12'),
    p('M2 17a1 1 0 0 0 .58.91l8.6 3.91a2 2 0 0 0 1.65 0l8.58-3.9A1 1 0 0 0 22 17')
  ),
  activity: lucide(
    p(
      'M22 12h-2.48a2 2 0 0 0-1.93 1.46l-2.35 8.36a.25.25 0 0 1-.48 0L9.24 2.18a.25.25 0 0 0-.48 0l-2.35 8.36A2 2 0 0 1 4.49 12H2'
    )
  ),
  tags: lucide(
    p('m15 5 6.3 6.3a2.4 2.4 0 0 1 0 3.4L17 19'),
    p(
      'M9.586 5.586A2 2 0 0 0 8.172 5H3a1 1 0 0 0-1 1v5.172a2 2 0 0 0 .586 1.414L8.29 18.29a2.426 2.426 0 0 0 3.42 0l3.58-3.58a2.426 2.426 0 0 0 0-3.42z'
    ),
    ['circle', { cx: '6.5', cy: '9.5', r: '.5', fill: 'currentColor' }]
  ),
  search: lucide(['circle', { cx: '11', cy: '11', r: '8' }], p('m21 21-4.3-4.3')),
  'book-open-text': lucide(
    p('M12 7v14'),
    p('M16 12h2'),
    p('M16 8h2'),
    p(
      'M3 18a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h5a4 4 0 0 1 4 4 4 4 0 0 1 4-4h5a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1h-6a3 3 0 0 0-3 3 3 3 0 0 0-3-3z'
    ),
    p('M6 12h2'),
    p('M6 8h2')
  ),
  x: lucide(p('M18 6 6 18'), p('m6 6 12 12')),
  warehouse: lucide(
    p(
      'M22 8.35V20a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V8.35A2 2 0 0 1 3.26 6.5l8-3.2a2 2 0 0 1 1.48 0l8 3.2A2 2 0 0 1 22 8.35Z'
    ),
    p('M6 18h12'),
    p('M6 14h12'),
    ['rect', { width: '12', height: '12', x: '6', y: '10' }]
  ),
  'file-text': lucide(
    p('M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z'),
    p('M14 2v4a2 2 0 0 0 2 2h4'),
    p('M10 9H8'),
    p('M16 13H8'),
    p('M16 17H8')
  ),
  'shopping-bag': lucide(
    p('M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z'),
    p('M3 6h18'),
    p('M16 10a4 4 0 0 1-8 0')
  ),
  // canideal.org Tools & Trust icons
  route: trust(p('M5 4v10a5 5 0 0 0 5 5h9M14 14l5 5-5 5M5 4h14M14 1l5 3-5 3')),
  document: trust(p('M5 2h10l4 4v16H5V2Zm10 0v5h4M8 11h8M8 15h8M8 19h5')),
  'shield-check': trust(p('m12 2 9 4v6c0 5-9 10-9 10S3 17 3 12V6l9-4Z'), p('m8 12 3 3 5-6')),
  people: trust(
    ['circle', { cx: '9', cy: '7', r: '3' }],
    p('M3 21v-3a6 6 0 0 1 12 0v3m1-17a3 3 0 0 1 0 6m2 4a5 5 0 0 1 3 4v3')
  ),
  cube: trust(p('m12 2 9 5-9 5-9-5 9-5ZM3 7v10l9 5 9-5V7M12 12v10')),
  message: trust(p('M4 4h16v12H9l-5 4V4Z'), p('M8 8h8M8 12h5')),
  chip: trust(
    ['rect', { x: '4', y: '4', width: '16', height: '16', rx: '5' }],
    p('M8 1v3m8-3v3M8 20v3m8-3v3M1 8h3m-3 8h3m16-8h3m-3 8h3')
  ),
  // Trade pages (canideal.org lib/trade-preview-pages.js ICONS).
  product: trust(p('m12 3 9 5-9 5-9-5 9-5Z'), p('M3 8v9l9 5 9-5V8M12 13v9')),
  outreach: trust(p('m3 10 17-6v16L3 14v-4Zm4 5 2 6h4l-2-5M20 8l2-1m-2 10 2 1')),
  offer: trust(
    ['rect', { x: '3', y: '5', width: '18', height: '15', rx: '3' }],
    p('m4 7 8 6 8-6M8 17h8')
  ),
  'check-square': trust(p('m4 12 5 5L20 6'), [
    'rect',
    { x: '2', y: '2', width: '20', height: '20', rx: '6' },
  ]),
  'search-plus': trust(['circle', { cx: '10', cy: '10', r: '7' }], p('m15 15 7 7M10 6v8M6 10h8')),
  'globe-lines': trust(
    ['circle', { cx: '12', cy: '12', r: '10' }],
    ['ellipse', { cx: '12', cy: '12', rx: '5', ry: '10' }],
    p('M2 12h20M4 6h16M4 18h16')
  ),
  'chart-bars': trust(p('M3 3v18h18M7 16v-5h3v5m3 0V7h3v9m3 0V4')),
  // Market Briefs audio tile (canideal.org design-previews/market-briefs/index.html).
  'audio-brief': {
    nodes: [
      p(
        'M14 35v-5a18 18 0 0 1 36 0v5M14 32h7v18h-7a4 4 0 0 1-4-4V36a4 4 0 0 1 4-4Zm36 0h-7v18h7a4 4 0 0 0 4-4V36a4 4 0 0 0-4-4ZM27 27v13m5-19v25m5-19v13'
      ),
    ],
    strokeWidth: 2,
    viewBox: '0 0 64 64',
    round: false,
  },
  // canideal.org Crop ROI Planner icons (crop.html)
  sprout: trust(p('M12 21v-9M12 15C4 15 3 9 3 4c6 0 9 3 9 8M12 17c0-8 3-11 9-11 0 6-3 11-9 11')),
  compass: trust(['circle', { cx: '12', cy: '12', r: '9' }], p('m16 8-2.5 5.5L8 16l2.5-5.5L16 8Z')),
  'buyer-fit': trust(
    ['circle', { cx: '12', cy: '7', r: '3' }],
    p('M5 21v-3a7 7 0 0 1 14 0v3M4 6a3 3 0 0 0 0 6M20 6a3 3 0 0 1 0 6')
  ),
  'price-chart': trust(p('M4 20V4M4 20h16M8 15l4-5 4 2 4-6')),
  'weather-risk': trust(
    p('M7 16a4 4 0 1 1 1-8 5 5 0 0 1 9 1 3.5 3.5 0 1 1 1 7H7ZM8 20h0M12 21h0M16 20h0')
  ),
  checklist: trust(p('m3 6 1 1 2-2M9 6h12M3 12l1 1 2-2M9 12h12M3 18l1 1 2-2M9 18h12')),
  'confidence-gap': trust(p('M12 3 2 20h20L12 3ZM12 9v4M12 17h0')),
  // canideal.org CanIDeal IQ icons (canideal-iq.html)
  'iq-chart': iq(p('M4 20V4m0 16h16M8 15l4-5 4 2 4-6')),
  'iq-next': iq(p('M5 12h14m-5-5 5 5-5 5')),
  'iq-engine': {
    ...iq(
      ['rect', { x: '10', y: '10', width: '28', height: '28', rx: '8' }],
      p('M18 18h12M18 24h8M18 30h12M17 4v6m14-6v6M17 38v6m14-6v6M4 17h6m-6 14h6m28-14h6m-6 14h6'),
      ['circle', { cx: '29', cy: '24', r: '1.5', fill: 'currentColor', stroke: 'none' }]
    ),
    strokeWidth: 1.4,
    viewBox: '0 0 48 48',
  },
} satisfies Record<string, CidIconDef>;

export type CidIconName = keyof typeof CID_ICONS;

export function isCidIconName(name: string): name is CidIconName {
  return Object.prototype.hasOwnProperty.call(CID_ICONS, name);
}
